import { Plus, X } from 'lucide-react'
import { forwardRef, useCallback, useMemo } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'

export interface MultiListFieldProps {
  value: string[]
  onChange: (value: string[]) => void
  disabled?: boolean
  fieldId: string
  inputClassName?: string
  placeholder?: string
}

export const MultiListField = forwardRef<HTMLInputElement, MultiListFieldProps>(function MultiListField(
  { value, onChange, disabled, fieldId, inputClassName, placeholder = 'Type an item...' },
  ref,
) {
  const items = useMemo(() => (value.length > 0 ? value : ['']), [value])

  const updateItem = useCallback(
    (index: number, text: string) => {
      const next = [...items]
      next[index] = text
      onChange(next)
    },
    [items, onChange],
  )

  const addItem = useCallback(() => {
    onChange([...items, ''])
  }, [items, onChange])

  const removeItem = useCallback(
    (index: number) => {
      const next = items.filter((_, i) => i !== index)
      onChange(next.length > 0 ? next : [''])
    },
    [items, onChange],
  )

  return (
    <div className="space-y-2">
      {items.map((item, index) => (
        <div key={index} className="flex items-center gap-2">
          <Input
            ref={index === 0 ? ref : undefined}
            id={index === 0 ? fieldId : undefined}
            value={item}
            onChange={(e) => updateItem(index, e.target.value)}
            disabled={disabled}
            placeholder={placeholder}
            className={inputClassName}
          />
          <button
            type="button"
            onClick={() => removeItem(index)}
            disabled={disabled || items.length <= 1}
            aria-label={`Remove item ${index + 1}`}
            className="shrink-0 p-1.5 text-muted-foreground hover:text-destructive disabled:opacity-30 disabled:cursor-not-allowed rounded-md transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      ))}
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={addItem}
        disabled={disabled}
        className="text-muted-foreground hover:text-foreground"
      >
        <Plus className="w-3.5 h-3.5 mr-1" />
        Add item
      </Button>
    </div>
  )
})
